import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IHttpClientService} from "data/services/http";
import {Locale} from "data/enums";
import {Bindings} from "data/constants/bindings";
import {makeAutoObservable, runInAction} from "mobx";
import {getLanguageFromPath} from "data/utils/language";
import type {IGeoStore} from "data/stores/geo/geo.store";

export interface IBootstrapController extends ViewController {
	get isReady(): boolean;
}

@injectable()
export class BootstrapController implements IBootstrapController {
	private _userLocale: Locale = navigator.language as Locale;
	private _isReady = false;

	constructor(
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.ApiHTTPClient) private _apiHTTPClient: IHttpClientService,
		@inject(Bindings.GeoStore) private _geoStore: IGeoStore,
		@inject(Bindings.GlobalApiHTTPClient) private _globalApiHTTPClient: IHttpClientService
	) {
		makeAutoObservable(this);
	}

	get isReady(): boolean {
		return this._isReady;
	}

	/**
	 * The method is to define a user's locale. It can be done by:
	 * 1) navigator.language
	 * 2) Site URL
	 * 3) Some JSON or API request settings
	 * 4) Whatever else
	 */
	private async defineLocale(): Promise<Locale> {
		const locale = getLanguageFromPath() === "es" ? Locale.ES_ES : Locale.EN_US;
		this._userLocale = await Promise.resolve(locale);
		return this._userLocale;
	}

	async init() {
		try {
			await this._i18nStore.switchLocale({
				locale: await this.defineLocale(),
			});
		} catch (_err) {
			// Show error message to a user
		}

		// Set locale that will be appended to each request
		this._apiHTTPClient.setLocale(this._i18nStore.lang);
		this._apiHTTPClient.setWIthPrefix(true);
		this._globalApiHTTPClient.setLocale("en");
		try {
			await this._geoStore.fetchGeo();
		} catch (e) {
			console.log(e);
		}

		runInAction(() => {
			this._isReady = true;
		});
	}
}
